import React, { useEffect, useState } from "react";
import { ChartContainer } from "../view/ChartContainer";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CustomTooltip } from "./components/CustomTooltip";
import { transformStackedBarData } from "./utils/chartDataTransformers";
import { VALIDATION_COLORS } from "./constants/chartColors";
import * as statisticService from "./statisticService";
import { AuditScope } from "./chartDynamicLabels";
import { InteractiveLegend } from "./components/InteractiveLegend";
import DashboardChartSkeleton from "./components/DashboardChartSkeleton";

function ChangesByDateChart({ fromDate, toDate, scope = AuditScope.BY_DAY }) {

  const [rechartsData, setRechartsData] = useState([]);
  const [hiddenSeries, setHiddenSeries] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const cdData = await statisticService.getChangesByDate(scope);
      if (cdData && cdData.labels && cdData.datasets) {
        // Transform data for Recharts
        const transformed = transformStackedBarData(cdData);
        setRechartsData(transformed);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (scope !== AuditScope.CUSTOM)
      fetchData();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [scope])


  const fetchDataCustom = async () => {
    setIsLoading(true);
    try {
      const cdData = await statisticService.getChangesByDateCustom(fromDate, toDate);
      if (cdData && cdData.labels && cdData.datasets) {

        // Transform data for Recharts
        const transformed = transformStackedBarData(cdData);
        setRechartsData(transformed);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (scope !== AuditScope.CUSTOM) {
      return;
    }

    const fromNotToday = isDateDifferentThanToday(fromDate);
    const toNotToday = isDateDifferentThanToday(toDate);
    checkCustomDatesSelected(fromNotToday, toNotToday);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [fromDate, toDate, scope])

  const checkCustomDatesSelected = (isFromDate, isToDate) => {
    if (isFromDate === true || isToDate === true) {
      fetchDataCustom();
    }
  }

  const isDateDifferentThanToday = (dateToCheck) => {
    if (!dateToCheck) {
      return false;
    }
    const today = new Date();
    const isSameDay =
      dateToCheck.getFullYear() === today.getFullYear() &&
      dateToCheck.getMonth() === today.getMonth() &&
      dateToCheck.getDate() === today.getDate();

    return !isSameDay;
  };

  // Handle legend click to toggle series visibility
  const handleToggleSeries = (dataKey) => {
    setHiddenSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  if (isLoading) {
    return <DashboardChartSkeleton title="Validation Results" subtitle="Configuration Changes" />;
  }

  return (
    <ChartContainer noHeader={true} className="oct_chart-card-v2">
      <div className="oct_chart-header-v2">
        <p className="oct_chart-subtitle-v2">Configuration Changes</p>
        <h3 className="oct_chart-title-v2">Validation Results</h3>
      </div>
      <div className="oct_interactive-legend-wrapper-v2">
        <InteractiveLegend
          payload={[
            { value: 'Suspicious', color: VALIDATION_COLORS.suspicious, dataKey: 'suspicious' },
            { value: 'Violation', color: VALIDATION_COLORS.violation, dataKey: 'violation' },
            { value: 'Normal', color: VALIDATION_COLORS.normal, dataKey: 'normal' }
          ]}
          hiddenSeries={hiddenSeries}
          onToggleSeries={handleToggleSeries}
        />
      </div>
      <div className="oct_recharts-wrapper-v2 oct_chart-no-interaction">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 10, height: 10 }}>
          <BarChart data={rechartsData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="label"
              stroke="#94a3b8"
              tick={{ fontSize: 12 }}
            />
            <YAxis
              stroke="#94a3b8"
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="suspicious"
              stackId="a"
              fill={VALIDATION_COLORS.suspicious}
              name="Suspicious"
              hide={hiddenSeries['suspicious']}
            />
            <Bar
              dataKey="violation"
              stackId="a"
              fill={VALIDATION_COLORS.violation}
              name="Violation"
              hide={hiddenSeries['violation']}
            />
            <Bar
              dataKey="normal"
              stackId="a"
              fill={VALIDATION_COLORS.normal}
              radius={[8, 8, 0, 0]}
              name="Normal"
              hide={hiddenSeries['normal']}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartContainer>
  )
}

export default ChangesByDateChart;
